import React, { ChangeEvent, FormEvent, useState } from 'react';
import {
  Calendar,
  Check,
  Clipboard,
  Clock,
  Heart,
  Mail,
  Phone,
  Stethoscope,
  User,
} from 'lucide-react';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';

interface AppointmentFormState {
  date: string;
  time: string;
  name: string;
  email: string;
  phone: string;
  doctor: string;
  appointmentType: string;
  reason: string;
  symptoms: string;
  medicalHistory: string;
}

const providerOptions = [
  'Dr. Smith - General Practice',
  'Dr. Johnson - Cardiology',
  'Dr. Williams - Pediatrics',
  'Dr. Brown - Dermatology',
  'Dr. Jones - Orthopedics',
];

const appointmentTypeOptions = [
  'Regular Check-up',
  'Follow-up',
  'New Patient Consultation',
  'Urgent Care',
  'Specialist Consultation',
  'Vaccination',
];

const initialAppointmentState: AppointmentFormState = {
  date: '',
  time: '',
  name: '',
  email: '',
  phone: '',
  doctor: '',
  appointmentType: '',
  reason: '',
  symptoms: '',
  medicalHistory: '',
};

const AppointmentScheduling: React.FC = () => {
  const [appointmentForm, setAppointmentForm] =
    useState<AppointmentFormState>(initialAppointmentState);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useDocumentMetadata(
    'Appointment Scheduling',
    'Collect appointment details and simulate a healthcare scheduling workflow in MediInsight AI.'
  );

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setAppointmentForm((currentState) => ({ ...currentState, [name]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);
    setIsSubmitted(false);

    if (
      !appointmentForm.date ||
      !appointmentForm.time ||
      !appointmentForm.name ||
      !appointmentForm.email ||
      !appointmentForm.phone ||
      !appointmentForm.doctor ||
      !appointmentForm.appointmentType
    ) {
      setErrorMessage('Please complete all required appointment details.');
      return;
    }

    try {
      // This demo flow intentionally simulates a submission while preserving the existing feature behavior.
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsSubmitted(true);
    } catch (error) {
      setErrorMessage('We could not schedule the appointment. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
        <div className="mb-8">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-sky-600">
            Care Coordination
          </p>
          <h1 className="mt-3 text-3xl font-bold text-slate-900">Schedule a Patient Appointment</h1>
          <p className="mt-3 max-w-2xl text-slate-600">
            Capture visit logistics, clinical context, and provider selection in a single intake workflow.
          </p>
        </div>

        {!isSubmitted ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <InputField label="Date*" htmlFor="date" icon={<Calendar className="h-5 w-5 text-sky-600" />}>
                <input
                  id="date"
                  name="date"
                  type="date"
                  value={appointmentForm.date}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  required
                />
              </InputField>

              <InputField label="Time*" htmlFor="time" icon={<Clock className="h-5 w-5 text-sky-600" />}>
                <input
                  id="time"
                  name="time"
                  type="time"
                  value={appointmentForm.time}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  required
                />
              </InputField>
            </div>

            <InputField label="Full Name*" htmlFor="name" icon={<User className="h-5 w-5 text-sky-600" />}>
              <input
                id="name"
                name="name"
                type="text"
                value={appointmentForm.name}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                required
              />
            </InputField>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <InputField label="Email*" htmlFor="email" icon={<Mail className="h-5 w-5 text-sky-600" />}>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={appointmentForm.email}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  required
                />
              </InputField>

              <InputField label="Phone Number*" htmlFor="phone" icon={<Phone className="h-5 w-5 text-sky-600" />}>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={appointmentForm.phone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  required
                />
              </InputField>
            </div>

            <InputField label="Select Provider*" htmlFor="doctor" icon={<Stethoscope className="h-5 w-5 text-sky-600" />}>
              <select
                id="doctor"
                name="doctor"
                value={appointmentForm.doctor}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                required
              >
                <option value="">Choose a provider</option>
                {providerOptions.map((provider) => (
                  <option key={provider} value={provider}>
                    {provider}
                  </option>
                ))}
              </select>
            </InputField>

            <InputField label="Appointment Type*" htmlFor="appointmentType" icon={<Clipboard className="h-5 w-5 text-sky-600" />}>
              <select
                id="appointmentType"
                name="appointmentType"
                value={appointmentForm.appointmentType}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                required
              >
                <option value="">Choose appointment type</option>
                {appointmentTypeOptions.map((appointmentType) => (
                  <option key={appointmentType} value={appointmentType}>
                    {appointmentType}
                  </option>
                ))}
              </select>
            </InputField>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <InputField label="Reason for Visit" htmlFor="reason" icon={<Clipboard className="h-5 w-5 text-sky-600" />}>
                <textarea
                  id="reason"
                  name="reason"
                  rows={4}
                  value={appointmentForm.reason}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
              </InputField>

              <InputField label="Current Symptoms" htmlFor="symptoms" icon={<Heart className="h-5 w-5 text-sky-600" />}>
                <textarea
                  id="symptoms"
                  name="symptoms"
                  rows={4}
                  value={appointmentForm.symptoms}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
              </InputField>

              <InputField label="Medical History" htmlFor="medicalHistory" icon={<Clipboard className="h-5 w-5 text-sky-600" />}>
                <textarea
                  id="medicalHistory"
                  name="medicalHistory"
                  rows={4}
                  value={appointmentForm.medicalHistory}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
              </InputField>
            </div>

            {errorMessage && <div className="text-sm text-rose-600">{errorMessage}</div>}

            <button
              type="submit"
              className="w-full rounded-full bg-sky-600 px-6 py-3 font-semibold text-white transition hover:bg-sky-700"
            >
              Confirm Appointment
            </button>
          </form>
        ) : (
          <div className="rounded-3xl bg-emerald-50 p-8 text-center">
            <Check className="mx-auto h-12 w-12 text-emerald-600" />
            <h2 className="mt-4 text-2xl font-semibold text-slate-900">Appointment Scheduled</h2>
            <p className="mt-3 text-slate-700">
              {appointmentForm.name}&apos;s appointment with {appointmentForm.doctor} is booked for {appointmentForm.date} at {appointmentForm.time}.
            </p>
            <p className="mt-2 text-slate-600">
              Visit type: {appointmentForm.appointmentType}
            </p>
            <p className="mt-4 text-sm text-slate-600">
              A confirmation message would be sent to {appointmentForm.email} in a production-ready workflow.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const InputField: React.FC<{
  label: string;
  htmlFor: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, htmlFor, icon, children }) => (
  <div>
    <label htmlFor={htmlFor} className="flex items-center gap-2 text-sm font-medium text-slate-700">
      {icon}
      {label}
    </label>
    {children}
  </div>
);

export default AppointmentScheduling;
